import { memo } from 'react'
import {
  Building2,
  Calendar,
  HelpCircle,
  Lightbulb,
  MapPin,
  Package,
  User,
} from 'lucide-react'
import { Handle, Position, type Node, type NodeProps } from '@xyflow/react'
import { cn } from '@/lib/utils'
import {
  ENTITY_NODE_HEIGHT,
  ENTITY_NODE_WIDTH,
  type EntityNodeData,
} from '@/utils/graph-data'

const entityTypeIcons = {
  person: User,
  place: MapPin,
  concept: Lightbulb,
  organisation: Building2,
  event: Calendar,
  object: Package,
  other: HelpCircle,
} as const

const entityTypeColors: Record<string, string> = {
  person: 'border-red-400 bg-red-50 dark:bg-red-950/40',
  place: 'border-green-400 bg-green-50 dark:bg-green-950/40',
  concept: 'border-amber-400 bg-amber-50 dark:bg-amber-950/40',
  organisation: 'border-violet-400 bg-violet-50 dark:bg-violet-950/40',
  event: 'border-pink-400 bg-pink-50 dark:bg-pink-950/40',
  object: 'border-muted-foreground/40 bg-muted/50',
  other: 'border-slate-400 bg-slate-50 dark:bg-slate-950/40',
}

function MemoryGraphEntityNodeComponent({
  data,
  selected,
}: NodeProps<Node<EntityNodeData>>) {
  const Icon =
    entityTypeIcons[data.entity_type as keyof typeof entityTypeIcons] ?? HelpCircle
  const colorClass = entityTypeColors[data.entity_type] ?? entityTypeColors.other

  return (
    <div
      className={cn(
        'flex items-center gap-1 rounded-md border px-2 py-1 shadow-sm',
        colorClass,
        selected && 'ring-2 ring-primary ring-offset-1 ring-offset-background',
      )}
      style={{ width: ENTITY_NODE_WIDTH, minHeight: ENTITY_NODE_HEIGHT }}
    >
      <Handle type="target" position={Position.Left} className="!h-2 !w-2 !bg-muted-foreground" />
      <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
      <span className="truncate text-[10px] font-medium">{data.label}</span>
      <Handle type="source" position={Position.Right} className="!h-2 !w-2 !bg-muted-foreground" />
    </div>
  )
}

export const MemoryGraphEntityNode = memo(MemoryGraphEntityNodeComponent)
